import React from 'react';

import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import {
	getBudget,
	updateBudget,
	deleteAllBudget,
	deleteBudget,
	getBudgetTableExport,
} from 'utils/apiRequests';
import { BudgetTable } from './BudgetTable';
import DownloadProgressBar from 'Data/DownloadData/DownloadProgressBar';
import { CommonDialog } from '../CommonDialog';
import { SecondaryNav } from '../SecondaryNav';
import { ImportData } from '../ImportData';
import { UNDO_ACTIONS, COLUMNS, getRowDataForSave } from './utils';
import { Wrapper, LoadingWrapper } from './BudgetTable.styles';

export class BudgetTableContainer extends React.Component {
	state = {
		isDeleteModalOpen: false,
		isImportModalOpen: false,
		errorMessage: null,
		undoHistory: [],
		isLoading: false,
		isNotificationShown: false,
		hasExportError: false,
		data: null,
		isExporting: false,
	};

	componentDidMount() {
		const { tableData, ignoreCache } = this.props;

		if (!tableData || ignoreCache) {
			this.fetchData();
		} else {
			this.setState({ data: tableData });
		}

		document.addEventListener('keydown', this.handleKeyDown);
	}

	componentWillUnmount() {
		document.removeEventListener('keydown', this.handleKeyDown);
	}

	handleKeyDown = event => {
		if (event.key.toLowerCase() !== 'z') {
			return;
		}

		const { undoHistory } = this.state;

		if (
			undoHistory.length > 0 &&
			((navigator.appVersion.indexOf('Mac') !== -1 && event.metaKey) ||
				(navigator.appVersion.indexOf('Mac') === -1 && event.ctrlKey))
		) {
			this.undo();
		}
	};

	fetchData = () => {
		const { setTableData, setIgnoreCache } = this.props;

		this.setState({ isLoading: true });

		return getBudget()
			.then(data => {
				setIgnoreCache(false);
				setTableData(data);
				this.setState({ data, isLoading: false });
			})
			.catch(e => {
				this.setState({ isLoading: false });
				this.showErrorModal(e.errorMessage || 'Failed to retrieve data');
			});
	};

	addNewRows = rowData => {
		const { setIgnoreCache } = this.props;

		return updateBudget(getRowDataForSave(rowData))
			.then(resp => {
				const { data } = this.state;

				setIgnoreCache(true);
				// Add new row to the start
				this.setState({ data: [...resp, ...data] });
			})
			.catch(e => {
				this.showErrorModal(e.errorMessage);
			});
	};

	editRow = rowData => {
		const { setIgnoreCache } = this.props;

		return updateBudget(getRowDataForSave(rowData))
			.then(() => {
				const { data } = this.state;

				setIgnoreCache(true);
				// Update existing rows in data
				this.setState({
					data: data.map(item => {
						const itemToUpdate = rowData.find(
							row => row[COLUMNS.ID] === item[COLUMNS.ID]
						);

						return itemToUpdate || item;
					}),
				});
			})
			.catch(e => {
				this.showErrorModal(e.errorMessage);

				throw e;
			});
	};

	deleteAllRows = () => {
		const { setIgnoreCache } = this.props;

		return deleteAllBudget()
			.then(() => {
				this.hideDeleteModal();
				setIgnoreCache(true);
				// Remove all data
				this.setState({ data: [] });
			})
			.catch(e => {
				this.showErrorModal(e.errorMessage);
			});
	};

	deleteRow = id => {
		const { setIgnoreCache } = this.props;

		return deleteBudget(id)
			.then(() => {
				const { data } = this.state;
				this.hideDeleteModal();
				setIgnoreCache(true);
				// Remove row with deleted id
				this.setState({ data: data.filter(item => item[COLUMNS.ID] !== id) });
			})
			.catch(e => {
				this.showErrorModal(e.errorMessage);
			});
	};

	showDeleteModal = () => {
		this.setState({ isDeleteModalOpen: true });
	};

	hideDeleteModal = () => {
		this.setState({ isDeleteModalOpen: false });
	};

	showImportModal = () => {
		this.setState({ isImportModalOpen: true });
	};

	hideImportModal = () => {
		this.setState({ isImportModalOpen: false });
	};

	showErrorModal = errorMessage => {
		this.setState({ errorMessage });
	};

	hideErrorModal = () => {
		this.setState({ errorMessage: null });
	};

	undo = suppressRequest => {
		const { undoHistory } = this.state;

		if (undoHistory.length === 0) {
			return;
		}

		const lastActionData = undoHistory.slice(-1)[0];
		const remainingUndos = undoHistory.slice(0, -1);

		this.setState({ undoHistory: remainingUndos });

		if (lastActionData.action === UNDO_ACTIONS.DELETE) {
			const rowsToCreate = lastActionData.rows.map(item => ({
				...item,
				[COLUMNS.ID]: undefined,
			}));
			this.addNewRows(rowsToCreate);

			return;
		}

		if (lastActionData.action === UNDO_ACTIONS.EDIT) {
			if (!suppressRequest) {
				this.editRow(lastActionData.rows);
			}

			return;
		}

		if (lastActionData.action === UNDO_ACTIONS.CREATE) {
			lastActionData.rows.forEach(row => {
				this.deleteRow(row[COLUMNS.ID]);
			});
		}
	};

	pushUndoAction = undoAction => {
		const { undoHistory } = this.state;

		this.setState({ undoHistory: [...undoHistory, undoAction] });
	};

	closeNotification = () => {
		this.setState({ isNotificationShown: false, hasExportError: false });
	};

	downloadFailed = () => {
		this.setState({ hasExportError: true });
	};

	clearDownloadError = () => {
		this.setState({ hasExportError: false });
	};

	showDownloadNotification = () => {
		this.setState({ isNotificationShown: true });

		setTimeout(() => {
			this.closeNotification();
		}, 5000);
	};

	handleExportAll = () => {
		this.setState({ isExporting: true });
		this.clearDownloadError();

		getBudgetTableExport()
			.catch(this.downloadFailed)
			.finally(() => {
				this.setState({ isExporting: false });
				this.showDownloadNotification();
			});
	};

	render() {
		const {
			data,
			isDeleteModalOpen,
			isImportModalOpen,
			errorMessage,
			undoHistory,
			isLoading,
			isNotificationShown,
			hasExportError,
			isExporting,
		} = this.state;
		const isUndoDisabled = undoHistory.length === 0;
		const isExportDisabled = !data || data.length === 0 || isExporting;

		return (
			<Wrapper>
				<SecondaryNav
					onExport={this.handleExportAll}
					onImport={this.showImportModal}
					isExportDisabled={isExportDisabled}
					isUndoDisabled={isUndoDisabled}
					onUndo={this.undo}
				/>
				{!data ? (
					<LoadingWrapper>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoadingWrapper>
				) : (
					<BudgetTable
						data={data}
						onAddNewRows={this.addNewRows}
						onDeleteAll={this.showDeleteModal}
						onDelete={this.deleteRow}
						onEdit={this.editRow}
						onUndo={this.undo}
						pushUndoAction={this.pushUndoAction}
						undoHistory={undoHistory}
						showError={this.showErrorModal}
						isLoading={isLoading}
					/>
				)}

				{errorMessage && (
					<CommonDialog
						onClose={this.hideErrorModal}
						body={errorMessage}
						title="Error"
						cancelText="Ok"
					/>
				)}
				{isImportModalOpen && (
					<CommonDialog
						onClose={this.hideImportModal}
						body={<ImportData fetchBudget={this.fetchData} />}
						title="Import data"
						maxWidth={640}
						suppressFooter
					/>
				)}
				{isDeleteModalOpen && (
					<CommonDialog
						onDelete={this.deleteAllRows}
						onClose={this.hideDeleteModal}
						body="You are about to permanently delete all data rows"
						title="Delete all data rows"
					/>
				)}
				{isNotificationShown && (
					<DownloadProgressBar
						error={hasExportError}
						successMessage="Generating file... You will receive a notification when the file has been generated"
						onClose={this.closeNotification}
					/>
				)}
			</Wrapper>
		);
	}
}
